/**
 * Centralized Image Security Service
 * Simple, lightweight security that can be imported anywhere
 * Enhanced with metadata and watermarking for traceability
 */

import { auth } from '@/firebase/config';
import { imageMetadataService } from './imageMetadataService';
import { isLikelyVideoUrl } from '@/utils/validation';

interface SecureImageOptions {
  imageUrl: string;
  wallpaperId: string;
  isPreview?: boolean;
}

interface SecureImageResult {
  secureUrl: string;
  isSecure: boolean;
  metadata?: any;
  fingerprint?: string;
}

class CentralizedImageSecurity {
  private readonly MAX_PREVIEW_QUALITY = 1.0; // Full quality for previews
  private readonly MAX_PREVIEW_WIDTH = 1200; // Higher resolution for previews
  private downloadCounts = new Map<string, number>();

  /**
   * Helper: build a Cloudinary transformed URL if the source is a Cloudinary link.
   * Inserts a transformation segment after `/upload/` using safe defaults.
   * For videos, uses video-appropriate transformations to avoid 400 errors.
   * Examples:
   *  - Images: c_fill,g_auto,ar_16:9,w_1200,f_auto,q_auto
   *  - Videos: c_fill,g_auto,w_1200,q_auto (no f_auto, no fl_progressive)
   */
  public static buildCloudinaryUrl(
    imageUrl: string,
    opts: { width?: number; height?: number; aspectRatio?: number; cropMode?: 'fill' | 'fit' | 'pad' }
  ): string {
    try {
      if (!imageUrl || !/res\.cloudinary\.com\//.test(imageUrl) || !/\/(image|video)\/upload\//.test(imageUrl)) {
        return imageUrl; // Not a Cloudinary URL
      }

      // Check if URL already has transformations (look for pattern /upload/.../v{version}/ or /upload/.../{filename})
      // If URL already has transformations between /upload/ and the version/filename, return as-is
      const uploadMatch = imageUrl.match(/\/upload\/([^/]+)\/(v\d+\/|.*\.[a-zA-Z]{3,4}$)/);
      if (uploadMatch && uploadMatch[1] && /[_,]/.test(uploadMatch[1])) {
        // URL already has transformations (contains commas or underscores which are transform separators)
        return imageUrl;
      }

      const isVideo = isLikelyVideoUrl(imageUrl);
      const crop = opts.cropMode || 'fill';
      const parts = imageUrl.split('/upload/');
      if (parts.length !== 2) return imageUrl;

      // Network-aware quality profile
      let qualityToken = 'q_auto';
      try {
        const connection = (navigator as any)?.connection || (navigator as any)?.mozConnection || (navigator as any)?.webkitConnection;
        const saveData = !!connection?.saveData;
        const effectiveType = String(connection?.effectiveType || '').toLowerCase();
        if (saveData) qualityToken = 'q_auto:eco';
        else if (/^2g$|^slow-2g$/.test(effectiveType)) qualityToken = 'q_auto:eco';
        else if (/3g/.test(effectiveType)) qualityToken = 'q_auto:low';
      } catch (_err) {
        // Fallback: keep default quality token
        qualityToken = 'q_auto';
      }

      const transforms: string[] = [];
      
      if (isVideo) {
        // For videos: use minimal, video-safe transformations
        // Do NOT use f_auto (format auto) or fl_progressive - these cause 400 errors on videos
        transforms.push(qualityToken);
        
        if (opts.width && opts.width > 0) transforms.push(`w_${Math.round(opts.width)}`);
        if (opts.height && opts.height > 0) transforms.push(`h_${Math.round(opts.height)}`);
        
        if (opts.aspectRatio && opts.aspectRatio > 0) {
          // Convert aspect ratio to simplified fraction (e.g., 0.56 -> 9:16)
          const { numerator, denominator } = this.simplifyAspectRatio(opts.aspectRatio);
          transforms.push(`ar_${numerator}:${denominator}`);
          transforms.push(`c_${crop}`);
          transforms.push('g_auto');
        } else if (crop && crop !== 'fit') {
          // Default crop for videos if not already set
          transforms.push(`c_${crop}`);
        }
      } else {
        // For images: use full image transformations
        transforms.push('f_auto', qualityToken, 'dpr_auto');
        
        if (opts.aspectRatio && opts.aspectRatio > 0) {
          // Convert aspect ratio to simplified fraction (e.g., 0.56 -> 9:16)
          const { numerator, denominator } = this.simplifyAspectRatio(opts.aspectRatio);
          transforms.push(`ar_${numerator}:${denominator}`);
          transforms.push(`c_${crop}`);
          transforms.push('g_auto');
        } else {
          if (crop) transforms.push(`c_${crop}`);
        }

        if (opts.width && opts.width > 0) transforms.push(`w_${Math.round(opts.width)}`);
        if (opts.height && opts.height > 0) transforms.push(`h_${Math.round(opts.height)}`);

        // Progressive enhancement for JPEG when f_auto selects it
        transforms.push('fl_progressive');
      }

      const transformStr = transforms.join(',');
      return `${parts[0]}/upload/${transformStr}/${parts[1]}`;
    } catch {
      return imageUrl;
    }
  }

  /**
   * Main method to get secure image URL
   * This is the ONLY method components need to call
   */
  async getSecureImageUrl(options: SecureImageOptions): Promise<SecureImageResult> {
  const { imageUrl, wallpaperId, isPreview = true } = options;

    try {
      if (isPreview && isLikelyVideoUrl(imageUrl)) {
        return {
          secureUrl: imageUrl,
          isSecure: true
        };
      }

      // For previews, create a lower quality version
      if (isPreview) {
  const secureUrl = await this.createSecurePreview(imageUrl, wallpaperId);
        return { secureUrl, isSecure: true };
      }

      // For downloads, check permissions and increment atomically
      if (this.canDownloadAndIncrement(wallpaperId)) {
        return { secureUrl: imageUrl, isSecure: true };
      } else {
        throw new Error('Download limit exceeded. Please create an account for unlimited downloads.');
      }
    } catch (error) {
      console.error('[CentralizedImageSecurity] Error:', error);
      
      // Check if this is a security/permission error
      if (error instanceof Error && error.message.includes('Download limit exceeded')) {
        // For security errors, DO NOT provide fallback - re-throw the error
        throw error;
      }
      
      // Only provide fallback for technical errors (image loading, canvas issues, etc.)
      // but NOT for security/permission errors
      if (isPreview) {
        // For preview errors, we can fallback to original URL
        return { secureUrl: imageUrl, isSecure: false };
      } else {
        // For download errors that aren't security-related, still fail
        throw error;
      }
    }
  }

  /**
   * NEW: Get secure download with metadata and watermarking for traceability
   * This method processes the image with metadata embedding and visible watermarks
   */
  async getSecureDownloadWithMetadata(options: SecureImageOptions): Promise<SecureImageResult> {
    const { imageUrl, wallpaperId } = options;

    try {
      // Check permissions and increment atomically
      if (!this.canDownloadAndIncrement(wallpaperId)) {
        throw new Error('Download limit exceeded. Please create an account for unlimited downloads.');
      }

      // For videos, skip canvas-based processing (videos can't be watermarked via canvas)
      if (isLikelyVideoUrl(imageUrl)) {
        // Generate minimal metadata for tracking
        const metadata = this.generateMinimalMetadata(wallpaperId);
        const fingerprint = await this.generateVideoFingerprint(imageUrl, metadata);

        // Log the download for audit trail
        imageMetadataService.logDownload(metadata, fingerprint);

        // Return the original video URL (Cloudinary handles video delivery)
        return {
          secureUrl: imageUrl,
          isSecure: true,
          metadata,
          fingerprint
        };
      }

      // Process image with metadata and watermarking
      const processedResult = await imageMetadataService.processImageForDownload(
        imageUrl,
        wallpaperId,
        {
          position: 'bottom-right',
          opacity: 0.7,
          includeTimestamp: true,
          includeUserId: true,
          text: 'Downloaded from WallpaperApp'
        }
      );

      // Create blob URL from processed image
      const secureUrl = URL.createObjectURL(processedResult.blob);

      // Log the download for audit trail
      imageMetadataService.logDownload(processedResult.metadata, processedResult.fingerprint);

      // Auto-cleanup blob URL after 5 minutes
      setTimeout(() => URL.revokeObjectURL(secureUrl), 5 * 60 * 1000);

      console.log('[CentralizedImageSecurity] Secure download with metadata created:', {
        wallpaperId,
        downloadId: processedResult.metadata.downloadId,
        fingerprint: processedResult.fingerprint,
        userId: processedResult.metadata.userId
      });

      return {
        secureUrl,
        isSecure: true,
        metadata: processedResult.metadata,
        fingerprint: processedResult.fingerprint
      };

    } catch (error) {
      console.error('[CentralizedImageSecurity] Secure download with metadata failed:', error);
      
      // Check if this is a security/permission error
      if (error instanceof Error && error.message.includes('Download limit exceeded')) {
        // For security errors, DO NOT provide fallback - re-throw the error
        throw error;
      }
      
      // For technical errors, still fail the download
      throw error;
    }
  }

  /**
   * Creates a secure preview with reduced quality and watermark
   * For anonymous users, this prevents right-click saving of full-quality images
   */
  private async createSecurePreview(imageUrl: string, _wallpaperId: string): Promise<string> {
    if (isLikelyVideoUrl(imageUrl)) {
      return CentralizedImageSecurity.buildCloudinaryUrl(imageUrl, {
        width: this.MAX_PREVIEW_WIDTH,
        cropMode: 'fit'
      });
    }

    const user = auth.currentUser;
    
    // For authenticated users, we can show higher quality previews
    const isAuthenticated = user && !user.isAnonymous;
  const quality = isAuthenticated ? 0.9 : this.MAX_PREVIEW_QUALITY;
  const maxWidth = isAuthenticated ? 800 : this.MAX_PREVIEW_WIDTH;
    
    return new Promise((resolve) => {
  const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            resolve(imageUrl); // Fallback to original
            return;
          }

          // Calculate dimensions based on user authentication
          const aspectRatio = img.height / img.width;
          const width = Math.min(maxWidth, img.width);
          const height = width * aspectRatio;

          canvas.width = width;
          canvas.height = height;

          // Draw image at calculated size
          ctx.drawImage(img, 0, 0, width, height);

          // Add watermark for anonymous users
          if (!isAuthenticated) {
            this.addWatermark(ctx, width, height);
          }

          // Convert to blob URL with appropriate quality
          canvas.toBlob((blob) => {
            if (blob) {
              const blobUrl = URL.createObjectURL(blob);
              // Store for cleanup - shorter time for anonymous users
              const cleanupTime = isAuthenticated ? 60 * 60 * 1000 : 30 * 60 * 1000; // 60min vs 30min
              setTimeout(() => URL.revokeObjectURL(blobUrl), cleanupTime);
              resolve(blobUrl);
            } else {
              resolve(imageUrl); // Fallback
            }
          }, 'image/jpeg', quality);

        } catch (error) {
          console.warn('[CentralizedImageSecurity] Preview creation failed:', error);
          resolve(imageUrl); // Fallback to original
        }
      };

      img.onerror = () => {
        console.warn('[CentralizedImageSecurity] Image load failed, using original URL');
        resolve(imageUrl); // Fallback to original
      };

  img.src = imageUrl;
    });
  }

  /**
   * Adds a subtle watermark to preview images
   */
  private addWatermark(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    ctx.save();
    ctx.globalAlpha = 0.1;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.font = `${Math.min(width, height) * 0.08}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('PREVIEW', width / 2, height / 2);
    ctx.restore();
  }

  /**
   * Atomically check if user can download and increment count if allowed
   * This prevents race conditions where multiple downloads bypass the limit
   */
  private canDownloadAndIncrement(_wallpaperId: string): boolean {
    const user = auth.currentUser;
    
    // Authenticated users can download (up to reasonable limits)
    if (user && !user.isAnonymous) {
      return true;
    }

    // Anonymous users: 3 downloads per day - check and increment atomically
    const today = new Date().toDateString();
    const key = `downloads_${today}`;
    const currentCount = parseInt(localStorage.getItem(key) || '0');
    
    // Check if limit would be exceeded
    if (currentCount >= 3) {
      return false;
    }
    
    // Increment count immediately to prevent race conditions
    const newCount = currentCount + 1;
    localStorage.setItem(key, newCount.toString());
    return true;
  }

  /**
   * Simplify decimal aspect ratio to integer fraction (e.g., 0.56 -> 9:16)
   */
  private static simplifyAspectRatio(ratio: number): { numerator: number; denominator: number } {
    // Common aspect ratio mappings for better accuracy
    const commonRatios: [number, number, number][] = [
      [1, 1, 1],           // 1:1
      [0.5, 1, 2],         // 1:2
      [0.5625, 9, 16],     // 9:16 (mobile portrait)
      [0.625, 10, 16],     // 10:16
      [0.6667, 2, 3],      // 2:3
      [0.75, 3, 4],        // 3:4
      [1.3333, 4, 3],      // 4:3
      [1.5, 3, 2],         // 3:2
      [1.7778, 16, 9],     // 16:9
      [2.3333, 21, 9],     // 21:9 (ultrawide)
      [2.39, 239, 100],    // 2.39:1 (cinematic)
    ];

    // Find closest common ratio
    for (const [commonRatio, num, den] of commonRatios) {
      if (Math.abs(ratio - commonRatio) < 0.02) {
        return { numerator: num, denominator: den };
      }
    }

    // Fallback: calculate using GCD
    const w = Math.round(ratio * 100);
    const h = 100;
    const gcd = this.greatestCommonDivisor(w, h);
    return { numerator: w / gcd, denominator: h / gcd };
  }

  /**
   * Calculate GCD for fraction simplification
   */
  private static greatestCommonDivisor(a: number, b: number): number {
    return b === 0 ? a : this.greatestCommonDivisor(b, a % b);
  }

  /**
   * Check if user can download (simple 3 download limit for anonymous users)
   * @deprecated Use canDownloadAndIncrement for actual downloads to prevent race conditions
   */
  private canDownload(_wallpaperId: string): boolean {
    const user = auth.currentUser;
    
    // Authenticated users can download (up to reasonable limits)
    if (user && !user.isAnonymous) {
      return true;
    }

    // Anonymous users: 3 downloads per day
    const today = new Date().toDateString();
    const key = `downloads_${today}`;
    const todayDownloads = parseInt(localStorage.getItem(key) || '0');
    
    return todayDownloads < 3;
  }

  /**
   * Increment download count for anonymous users
   * @deprecated Use canDownloadAndIncrement for actual downloads to prevent race conditions
   */
  private incrementDownloadCount(_wallpaperId: string): void {
    const user = auth.currentUser;
    
    if (!user || user.isAnonymous) {
      const today = new Date().toDateString();
      const key = `downloads_${today}`;
      const currentCount = parseInt(localStorage.getItem(key) || '0');
      localStorage.setItem(key, (currentCount + 1).toString());
      
      console.log(`[CentralizedImageSecurity] Download count: ${currentCount + 1}/3`);
    }
  }

  /**
   * Get remaining downloads for anonymous users
   */
  getRemainingDownloads(): { remaining: number; total: number; isAuthenticated: boolean } {
    const user = auth.currentUser;
    
    if (user && !user.isAnonymous) {
      return { remaining: -1, total: 100, isAuthenticated: true }; // Unlimited for auth users
    }

    const today = new Date().toDateString();
    const key = `downloads_${today}`;
    const todayDownloads = parseInt(localStorage.getItem(key) || '0');
    
    return {
      remaining: Math.max(0, 3 - todayDownloads),
      total: 3,
      isAuthenticated: false
    };
  }

  /**
   * Generate minimal metadata for video downloads (since we can't watermark videos via canvas)
   */
  private generateMinimalMetadata(wallpaperId: string): any {
    const user = auth.currentUser;
    return {
      wallpaperId,
      userId: user?.uid,
      userEmail: user?.email || undefined,
      downloadTimestamp: Date.now(),
      downloadId: this.generateDownloadId(),
      userAgent: navigator.userAgent,
      sessionId: this.getOrCreateSessionId(),
      isVideo: true
    };
  }

  /**
   * Generate unique download ID
   */
  private generateDownloadId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `${timestamp}-${random}`;
  }

  /**
   * Get or create session ID
   */
  private getOrCreateSessionId(): string {
    const key = 'wallpaper_session_id';
    let sessionId = sessionStorage.getItem(key);
    if (!sessionId) {
      sessionId = this.generateDownloadId();
      sessionStorage.setItem(key, sessionId);
    }
    return sessionId;
  }

  /**
   * Generate fingerprint for video downloads
   */
  private async generateVideoFingerprint(videoUrl: string, metadata: any): Promise<string> {
    try {
      const urlHash = await this.hashString(videoUrl);
      const metadataHash = await this.hashString(JSON.stringify(metadata));
      return `${urlHash.substring(0, 16)}-${metadataHash.substring(0, 16)}`;
    } catch (error) {
      console.warn('[CentralizedImageSecurity] Failed to generate video fingerprint:', error);
      return this.generateDownloadId();
    }
  }

  /**
   * Hash a string using SHA-256
   */
  private async hashString(str: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      // Fallback to simple hash
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return Math.abs(hash).toString(36);
    }
  }

  /**
   * Apply comprehensive security protections to any image element
   */
  protectImageElement(element: HTMLElement): void {
    // Disable right-click context menu
    element.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    // Disable drag and drop
    element.addEventListener('dragstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    });

    // Disable selection
    element.style.userSelect = 'none';
    (element.style as any).webkitUserSelect = 'none';
    (element.style as any).mozUserSelect = 'none';
    (element.style as any).msUserSelect = 'none';

    // Disable pointer events for overlays (but keep click events)
    element.style.pointerEvents = 'auto';

    // Add additional security attributes
    element.setAttribute('draggable', 'false');
    element.setAttribute('oncontextmenu', 'return false');
    element.setAttribute('onselectstart', 'return false');
    element.setAttribute('ondragstart', 'return false');

    // Prevent keyboard shortcuts for saving images
    element.addEventListener('keydown', (e) => {
      // Prevent Ctrl+S, Ctrl+Shift+S, F12, etc.
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Prevent F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Prevent Ctrl+Shift+I (DevTools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Add visual indicator for protected images (subtle)
    element.style.position = 'relative';
    
    // Create invisible overlay to prevent direct interaction
    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.zIndex = '1';
    overlay.style.pointerEvents = 'none'; // Allow clicks through but prevent context menu
    overlay.style.userSelect = 'none';
    
    // Add the overlay if the element has a parent
    if (element.parentElement) {
      element.parentElement.style.position = 'relative';
      element.parentElement.appendChild(overlay);
    }
  }

  /**
   * Apply comparable protections to a video element.
   * Videos cannot be watermarked via canvas here, so we reuse the same interaction guards.
   */
  protectVideoElement(element: HTMLVideoElement): void {
    this.protectImageElement(element)
  }

  /**
   * Create a secure image component wrapper
   */
  async createSecureImageWrapper(imageUrl: string, wallpaperId: string, options: {
    className?: string;
    alt?: string;
    onClick?: () => void;
  } = {}): Promise<HTMLElement> {
    try {
      // Get secure URL
      const secureResult = await this.getSecureImageUrl({
        imageUrl,
        wallpaperId,
        isPreview: true
      });

      // Create wrapper div
      const wrapper = document.createElement('div');
      wrapper.className = options.className || '';
      wrapper.style.position = 'relative';
      wrapper.style.display = 'inline-block';

      // Create image element
      const img = document.createElement('img');
      img.src = secureResult.secureUrl;
      img.alt = options.alt || 'Secure wallpaper preview';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';

      // Apply security protections
      this.protectImageElement(img);

      // Add click handler if provided
      if (options.onClick) {
        img.addEventListener('click', options.onClick);
        img.style.cursor = 'pointer';
      }

      // Add image to wrapper
      wrapper.appendChild(img);

      return wrapper;
    } catch (error) {
      console.error('[CentralizedImageSecurity] Failed to create secure wrapper:', error);
      
      // Fallback: create basic wrapper with original URL
      const wrapper = document.createElement('div');
      wrapper.className = options.className || '';
      
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = options.alt || 'Wallpaper preview';
      
      // Still apply basic protections
      this.protectImageElement(img);
      
      wrapper.appendChild(img);
      return wrapper;
    }
  }

  /**
   * Reset download count (for testing)
   */
  resetDownloadCount(): void {
    if (process.env.NODE_ENV === 'development') {
      const today = new Date().toDateString();
      const key = `downloads_${today}`;
      localStorage.removeItem(key);
      console.log('[CentralizedImageSecurity] Download count reset');
    }
  }
}

// Convenience helper export for components
export function transformCloudinaryUrl(
  imageUrl: string,
  opts: { width?: number; height?: number; aspectRatio?: number; cropMode?: 'fill' | 'fit' | 'pad' }
): string {
  return CentralizedImageSecurity.buildCloudinaryUrl(imageUrl, opts)
}

// Export singleton instance
export const centralizedImageSecurity = new CentralizedImageSecurity();

// Make available globally for testing
if (process.env.NODE_ENV === 'development') {
  (window as any).centralizedImageSecurity = centralizedImageSecurity;
}